(function () {
  // ---------- Config (amounts in cents) ----------
  const TIERS = [
    { amount: 2000, label: '1× Seeds' },
    { amount: 4000, label: '1× Spice Bag + 1× Seeds' },
    { amount: 6000, label: '2× Spice Bags + 2× Seeds' },
    { amount: 8000, label: '1× Sweet Chili + 2× Spice Bags + 2× Seeds' }
  ];

  // Reminder copy (edit this if you want different wording)
  const REMINDER_TEXT = '✨ Don’t forget to add your free goodies before checkout!';

  // ---------- Debug helpers ----------
  const DBG_ON = () => localStorage.getItem('cpDebug') === '1';
  const log = (...a) => { if (DBG_ON()) console.log('[cart-progress]', ...a); };
  const warn = (...a) => { if (DBG_ON()) console.warn('[cart-progress]', ...a); };

  // ---------- Global state ----------
  let prevSubtotalGlobal = 0;     // previous subtotal across renders
  let lastCelebratedAmount = 0;   // last tier amount we celebrated
  let lastKnownSubtotal = 0;      // ignore bogus zeros from /cart.js

  // ---------- Utilities ----------
  const clamp01 = (x) => Math.max(0, Math.min(1, x));
  const setFill = (fillEl, percent01) => {
    if (!fillEl) return;
    const p = clamp01(percent01);
    fillEl.style.setProperty('--x', String(p));
    fillEl.style.transform = `scaleX(${p})`;
  };

  const getSubtotalFromCart = (cart) => {
    let sub = 0;
    if (cart && typeof cart.items_subtotal_price === 'number') sub = cart.items_subtotal_price;
    else if (cart && typeof cart.total_price === 'number') sub = cart.total_price;

    // Anti-flap: if subtotal is 0 but items exist, keep last known
    if (sub === 0 && cart && cart.item_count > 0) {
      log('anti-flap: got 0 with items>0; using lastKnownSubtotal', lastKnownSubtotal);
      sub = lastKnownSubtotal || 0;
    } else if (sub > 0 || (cart && cart.item_count === 0)) {
      lastKnownSubtotal = sub;
    }
    return sub;
  };

  const nextTierCopy = (nextTier, subtotal) => {
    if (!nextTier) return '🎉 All gifts unlocked';
    const remaining = nextTier.amount - subtotal;
    return `€${Math.ceil(remaining / 100)} away from: ${nextTier.label}`;
  };

  // Create or update the reminder line under the bar for a given container
  function ensureReminder(container, subtotal) {
    if (!container) return;
    let el = container.querySelector('.cart-progress__reminder');
    if (!el) {
      el = document.createElement('div');
      el.className = 'cart-progress__reminder';
      el.textContent = REMINDER_TEXT;
      // place after the bar (before tiers/ctas if any)
      const bar = container.querySelector('.cart-progress__bar');
      if (bar && bar.parentNode) {
        bar.parentNode.insertBefore(el, bar.nextSibling);
      } else {
        container.appendChild(el);
      }
    }
    // Only show once the first tier is reachable/nearby (≥ €20)
    el.style.display = subtotal >= TIERS[0].amount ? 'block' : 'none';
  }

  // ---------- Core render ----------
  function renderOne(container, subtotal, crossedTier) {
    if (!container) return;
    const status = container.querySelector('.cart-progress__status');
    const next   = container.querySelector('.cart-progress__next');
    const fill   = container.querySelector('.cart-progress__fill');

    container.classList.add('is-ready');

    const unlocked = TIERS.filter(t => subtotal >= t.amount);
    const nextTier = TIERS.find(t => subtotal < t.amount);

    // % toward next
    let percent01 = 1;
    if (nextTier) {
      const base = unlocked.length ? unlocked[unlocked.length - 1].amount : 0;
      const span = nextTier.amount - base;
      percent01 = span > 0 ? clamp01((subtotal - base) / span) : 1;
    }
    setFill(fill, percent01);

    // Celebration lock per element
    const now = Date.now();
    const lockUntil = parseInt(container.dataset.lockUntil || '0', 10);
    const locked = now < lockUntil;

    if (crossedTier && !locked) {
      container.classList.add('celebrate');
      container.dataset.lockUntil = String(now + 2600); // 2.6s lock
      if (status) status.textContent = `🎉 Unlocked: ${crossedTier.label}`;
      if (next) next.textContent = '';
      setTimeout(() => {
        container.classList.remove('celebrate');
        if (status) status.textContent = nextTierCopy(nextTier, subtotal);
        if (next) next.textContent = '';
      }, 2500);
    } else if (!locked) {
      if (status) status.textContent = nextTierCopy(nextTier, subtotal);
      if (next) next.textContent = '';
    }

    // Ensure reminder visibility based on current subtotal
    ensureReminder(container, subtotal);
  }

  function renderAll(subtotal, crossedTier) {
    document.querySelectorAll('.cart-progress').forEach(el => renderOne(el, subtotal, crossedTier));
  }

  // ---------- Update pipeline ----------
  function processSubtotal(newSubtotal) {
    // Determine if we crossed a tier since the last known subtotal
    const crossed = TIERS.find(t =>
      prevSubtotalGlobal < t.amount &&
      newSubtotal >= t.amount &&
      t.amount > lastCelebratedAmount
    );

    log('subtotal update:', 'prev=', prevSubtotalGlobal, 'new=', newSubtotal, 'crossed=',
        crossed ? crossed.amount : null);

    renderAll(newSubtotal, crossed || null);

    if (crossed) lastCelebratedAmount = crossed.amount;
    prevSubtotalGlobal = newSubtotal;
  }

  async function fetchAndUpdate(tag = 'fetch') {
    try {
      const r = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!r.ok) { warn(tag, 'fetch failed', r.status); return; }
      const cart = await r.json();
      const subtotal = getSubtotalFromCart(cart);
      log(tag, 'cart.js subtotal=', subtotal, 'item_count=', cart.item_count);
      processSubtotal(subtotal);
    } catch (e) {
      warn(tag, 'fetch error', e);
    }
  }

  // ---------- Init & resilience ----------
  function initOne(el) {
    if (el.dataset.initialised) return;
    const initial = parseInt(el.dataset.subtotal || '0', 10);
    lastKnownSubtotal = initial || lastKnownSubtotal;
    el.dataset.initialised = '1';
  }

  function initAll() {
    document.querySelectorAll('.cart-progress').forEach(initOne);
  }

  function boot() { initAll(); fetchAndUpdate('boot'); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // When DOM re-renders (drawer open/close), re-init and hydrate
  const mo = new MutationObserver((muts) => {
    let added = false;
    muts.forEach(m => {
      m.addedNodes && m.addedNodes.forEach(node => {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches('.cart-progress')) { initOne(node); added = true; }
        node.querySelectorAll && node.querySelectorAll('.cart-progress').forEach(n => { initOne(n); added = true; });
      });
    });
    if (added) {
      log('MO: new progress node(s) → hydrate');
      const t0 = Date.now();
      const id = setInterval(() => {
        fetchAndUpdate('moPoll');
        if (Date.now() - t0 > 2000) clearInterval(id);
      }, 300);
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });

  // Theme events (if present)
  document.addEventListener('cart:updated', (e) => {
    const cart = e.detail && e.detail.cart;
    if (cart) {
      const subtotal = getSubtotalFromCart(cart);
      log('event cart:updated subtotal=', subtotal);
      processSubtotal(subtotal || 0);
    } else {
      fetchAndUpdate('evtFallback');
    }
  });
  document.addEventListener('cart:refresh-progress', () => fetchAndUpdate('manual'));

  // Safety net: common cart actions
  document.body.addEventListener('click', (e) => {
    const t = e.target;
    if (!t) return;
    if (
      t.closest('[name="add"]') ||
      t.closest('[data-cart-quantity]') ||
      t.closest('button[name="plus"]') ||
      t.closest('button[name="minus"]') ||
      t.closest('.quantity__button') ||
      t.closest('[data-update-cart]') ||
      t.closest('[data-remove]') ||
      t.closest('form[action^="/cart"]')
    ) {
      log('click → fetch');
      setTimeout(() => fetchAndUpdate('click250'), 250);
      setTimeout(() => fetchAndUpdate('click900'), 900);
    }
  });

  // Watchdog: if any ready widget still says "Calculating…", hydrate
  setInterval(() => {
    document.querySelectorAll('.cart-progress.is-ready .cart-progress__status').forEach(st => {
      if (/Calculating/i.test(st.textContent || '')) {
        log('watchdog: still calculating → fetch');
        fetchAndUpdate('watchdog');
      }
    });
  }, 1200);

  // Expose a tiny debug helper
  window.cpState = () => ({
    prevSubtotalGlobal, lastCelebratedAmount, lastKnownSubtotal
  });
})();
